import {
  type ApiEventPattern,
  type ReduxEventMapping,
  pathExact,
  pathMatches,
  createApiResponseLogger,
  createAnalyticsMiddleware,
} from '@center-technologies/analytics';

/**
 * Center-PWA-specific analytics configuration.
 *
 * Adds API URL → event mappings for the routes the PWA hits across
 * graph-service, schema-service, etc., plus Redux thunk → event mappings
 * for the Center-PWA store. Other apps (center-auth) provide their own.
 */

const obj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
const str = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;
const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined;

/**
 * Read a decoded route param out of a request path by 0-based segment index (leading slash
 * stripped). Route params are not present in the request body for parameterized calls that send
 * no body at all — e.g. `POST /compositions/:systemName/versions/:v/publish`.
 */
const segment = (path: string, index: number): string | undefined => {
  const raw = path.replace(/^\//, '').split('/')[index];
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const PATTERNS: ApiEventPattern[] = [
  // ─── Spheres ────────────────────────────────────────────────────────
  {
    method: 'POST',
    match: pathExact('/spheres'),
    event: 'sphere_created',
    extract: (body, data) => ({
      schemaSystemName: str(obj(body)?.schemaSystemName),
      parentSphereId: str(obj(body)?.parentSphereId),
      sphereId: str(obj(data)?.id),
    }),
    value: (_body, data) => str(obj(data)?.id),
  },
  {
    method: 'PATCH',
    match: pathMatches(/^\/spheres\/[^/]+$/),
    event: 'sphere_updated',
  },
  // ─── Purpose ────────────────────────────────────────────────────────
  // Metadata is the sphere id and counts only: never statement text, and never a sphere name
  // (a subject sphere is a person).
  //
  // `purpose_statement_saved`, `purpose_affirmed` and `purpose_parent_changed` are NOT here.
  // Their metadata (`hadStatement`, `unchanged`, `accessChanged`) is before-state an interceptor
  // watching one request/response pair cannot see, so each is emitted with `useAnalytics()` at the
  // call site that holds it — PurposeSection and PurposeParentDialogMolecule. The three events
  // below carry only what the path and response already say.
  {
    method: 'POST',
    match: pathMatches(/^\/spheres\/[^/]+\/purpose\/parent\/preview$/),
    event: 'purpose_parent_previewed',
    extract: (_body, data, path) => ({
      sphereLabel: segment(path, 1),
      accessChanges: obj(data)?.accessChanges === true,
      gainsCount: num(obj(obj(data)?.gains)?.count),
      losesCount: num(obj(obj(data)?.loses)?.count),
      truncated: obj(data)?.truncated === true,
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/spheres\/[^/]+\/purpose\/links$/),
    event: 'purpose_link_added',
    extract: (_body, _data, path) => ({ sphereLabel: segment(path, 1) }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/spheres\/[^/]+\/purpose\/links\/[^/]+$/),
    event: 'purpose_link_removed',
    extract: (_body, _data, path) => ({ sphereLabel: segment(path, 1) }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/spheres\/[^/]+$/),
    event: 'sphere_deleted',
  },

  // Outcome event only: which sphere kind changed, and how big the change was. The server's
  // `change` summary carries counts, so no Archetype system_name ever leaves the client for a
  // person sphere.
  {
    method: 'PUT',
    match: pathMatches(/^\/spheres\/[^/]+\/archetypes$/),
    event: 'sphere_archetypes_updated',
    extract: (body, data) => {
      const change = obj(obj(data)?.change);
      const submitted = obj(body)?.archetypes;
      return {
        sphereLabel: str(change?.sphereLabel),
        count: Array.isArray(submitted) ? submitted.length : undefined,
        added: num(change?.added),
        removed: num(change?.removed),
        primaryChanged: change?.primaryChanged === true,
      };
    },
  },

  // ─── Identities ─────────────────────────────────────────────────────
  {
    method: 'PATCH',
    match: pathMatches(/^\/identities\/[^/]+\/profile$/),
    event: 'profile_updated',
    extract: (body) => ({
      displayNameChanged: obj(body)?.displayName !== undefined,
      phoneChanged: obj(body)?.phone !== undefined,
      preferredLanguageChanged: obj(body)?.preferredLanguage !== undefined,
    }),
  },
  {
    method: 'PATCH',
    match: pathMatches(/^\/identities\/[^/]+\/preferences$/),
    event: 'notification_channel_toggled',
    extract: (body) => ({
      inApp: obj(obj(obj(body)?.notifications)?.notificationChannels)?.inApp,
      email: obj(obj(obj(body)?.notifications)?.notificationChannels)?.email,
      sms: obj(obj(obj(body)?.notifications)?.notificationChannels)?.sms,
    }),
  },

  // ─── Approaches & deployments ──────────────────────────────────────
  {
    method: 'POST',
    match: pathMatches(/^\/spheres\/[^/]+\/deployments$/),
    event: 'approach_deployed',
    extract: (body) => ({
      approachSystemName: str(obj(body)?.approachSystemName),
      approachVersion: str(obj(body)?.approachVersion),
    }),
  },
  {
    method: 'POST',
    match: pathExact('/approaches'),
    event: 'approach_created',
    extract: (body) => ({
      systemName: str(obj(body)?.systemName),
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/approaches\/[^/]+\/publish$/),
    event: 'approach_published',
  },

  // ─── Compositions & conformance (EAINT-891 Phase 3) ────────────────
  {
    method: 'POST',
    match: pathMatches(/^\/compositions\/[^/]+\/versions\/\d+\/publish$/),
    status: 409,
    event: 'composition_publish_blocked',
    extract: (body, data, path) => {
      const impact = obj(data)?.impact ? obj(obj(data)?.impact) : undefined;
      const diff = obj(impact?.diff);
      const dependents = Array.isArray(impact?.dependents) ? (impact?.dependents as unknown[]) : [];
      return {
        systemName: segment(path, 1),
        breakingCount: num((diff?.breaking as unknown[] | undefined)?.length),
        failingDependents: num(dependents.filter((d) => obj(d)?.status === 'fail').length),
      };
    },
  },
  {
    method: 'POST',
    match: pathMatches(/^\/compositions\/[^/]+\/versions\/\d+\/publish$/),
    event: 'composition_published',
    extract: (body, data, path) => {
      const impact = obj(obj(data)?.impact);
      const diff = obj(impact?.diff);
      const dependents = Array.isArray(impact?.dependents) ? (impact?.dependents as unknown[]) : [];
      return {
        systemName: str(obj(obj(data)?.composition)?.system_name) ?? segment(path, 1),
        version: num(obj(obj(data)?.composition)?.version),
        additiveCount: num((diff?.additive as unknown[] | undefined)?.length),
        breakingCount: num((diff?.breaking as unknown[] | undefined)?.length),
        rebasedDependents: num(dependents.filter((d) => obj(d)?.status === 'pass').length),
      };
    },
  },

  // ─── Arcs ───────────────────────────────────────────────────────────
  {
    method: 'PATCH',
    match: pathMatches(/^\/arcs\/[^/]+\/transition$/),
    event: 'arc_stage_transitioned',
    extract: (body) => ({
      toStage: str(obj(body)?.newStageId ?? obj(body)?.toStage),
      comment: str(obj(body)?.comment),
    }),
  },

  // ─── Channels & marketplace ────────────────────────────────────────
  {
    method: 'POST',
    match: pathExact('/channels'),
    event: 'channel_created',
    extract: (body) => ({
      scope: str(obj(body)?.scope),
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/channels\/[^/]+\/subscribe$/),
    event: 'channel_subscribed',
  },
  {
    // Subscribing an organization sphere to a channel from the sphere picker.
    method: 'POST',
    match: pathExact('/channels/sphere-subscriptions'),
    event: 'channel_sphere_subscribed',
    extract: (body) => ({
      channelId: str(obj(body)?.channelId),
      sphereId: str(obj(body)?.sphereId),
      scopeMode: str(obj(body)?.scopeMode),
    }),
  },
  {
    method: 'POST',
    match: pathExact('/channel-items/import'),
    event: 'channel_item_imported',
    extract: (body) => ({
      sourceChannelSystemName: str(obj(body)?.sourceChannelSystemName),
      itemType: str(obj(body)?.itemType),
    }),
  },
  {
    // Publishing a template-supertype item (or approach) to a channel.
    method: 'POST',
    match: pathMatches(/^\/channels\/[^/]+\/items$/),
    event: 'channel_item_published',
    extract: (body, data) => ({
      itemType: str(obj(body)?.itemType),
      channelSystemName: str(obj(obj(data)?.item)?.channel_system_name),
    }),
  },
  {
    // Reference (subscribe) — writes a subscriber tuple on the author's row,
    // distinct from the legacy `/channel-items/import` copy-on-consume path.
    method: 'POST',
    match: pathMatches(/^\/channels\/[^/]+\/items\/[^/]+\/import$/),
    event: 'channel_item_referenced',
    extract: (_body, data) => ({
      itemType: str(obj(data)?.itemType),
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/channels\/[^/]+\/items\/[^/]+\/fork$/),
    event: 'channel_item_forked',
    extract: (_body, data) => ({
      forkedContentType: str(obj(data)?.forkedContentType),
    }),
  },

  // ─── Perspectives ──────────────────────────────────────────────────
  // Metadata is the perspective system name at most: never a subject id, a person's name or a label.
  {
    method: 'POST',
    match: pathMatches(/^\/perspectives\/[^/]+\/assign-subject$/),
    event: 'perspective_assigned',
    extract: () => ({}),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/spheres\/[^/]+\/perspectives\/assign-subject$/),
    event: 'perspective_assigned',
    extract: (body) => ({
      perspectiveSystemName: str(obj(body)?.perspectiveSystemName),
    }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/perspectives\/[^/]+\/subjects\/[^/]+$/),
    event: 'perspective_unassigned',
    extract: () => ({}),
  },

  // ─── Measures ───────────────────────────────────────────────────────
  {
    method: 'PATCH',
    match: pathMatches(/^\/spheres\/[^/]+\/measure-members\/[^/]+$/),
    event: 'measure_member_weight_updated',
    extract: (body, data) => ({
      memberSphereId: str(obj(data)?.sphereId),
      weight: num(obj(body)?.weight),
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/spheres\/[^/]+\/measure-members\/[^/]+$/),
    event: 'measure_member_link_changed',
    extract: () => ({ action: 'link' }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/spheres\/[^/]+\/measure-members\/[^/]+$/),
    event: 'measure_member_link_changed',
    extract: () => ({ action: 'unlink' }),
  },

  // ─── Marketplace logos ─────────────────────────────────────────────
  {
    method: 'PUT',
    match: pathMatches(/^\/publishers\/[^/]+\/logo$/),
    event: 'publisher_logo_uploaded',
    extract: (_body, data) => ({
      attachmentId: str(obj(obj(data)?.attachment)?.id),
    }),
  },
  {
    method: 'PUT',
    match: pathMatches(/^\/publishers\/[^/]+\/banner$/),
    event: 'publisher_banner_uploaded',
    extract: (_body, data) => ({
      attachmentId: str(obj(obj(data)?.attachment)?.id),
    }),
  },
  {
    method: 'PUT',
    match: pathMatches(/^\/channels\/[^/]+\/logo$/),
    event: 'channel_logo_uploaded',
    extract: (_body, data) => ({
      attachmentId: str(obj(obj(data)?.attachment)?.id),
    }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/(publishers|channels)\/[^/]+\/(logo|banner)$/),
    event: 'marketplace_logo_removed',
  },

  // ─── Publisher members ─────────────────────────────────────────────
  {
    method: 'POST',
    match: pathMatches(/^\/publishers\/[^/]+\/members$/),
    event: 'publisher_member_added',
    extract: (body) => ({
      role: str(obj(body)?.role),
    }),
  },
  {
    method: 'PUT',
    match: pathMatches(/^\/publishers\/[^/]+\/members\/[^/]+$/),
    event: 'publisher_member_role_changed',
    extract: (body) => ({
      role: str(obj(body)?.role),
    }),
  },
  {
    method: 'DELETE',
    match: pathMatches(/^\/publishers\/[^/]+\/members\/[^/]+$/),
    event: 'publisher_member_removed',
  },

  // ─── Search ────────────────────────────────────────────────────────
  {
    method: 'POST',
    match: pathExact('/search/marketplace'),
    event: 'marketplace_search',
    extract: (body, data) => ({
      query: str(obj(body)?.query),
      itemType: str(obj(body)?.itemType),
      resultCount: num(obj(data)?.total),
    }),
  },
  {
    method: 'POST',
    match: pathExact('/search'),
    event: 'sphere_search',
    extract: (body, data) => ({
      query: str(obj(body)?.query),
      // Source-tab dimension (all|recent|nearby|responsible). The picker
      // re-queries /search on every source-tab change, so this captures
      // source-tab usage via the existing API event (design-system components
      // have no component-level analytics hook to fire a discrete UI event).
      source: str(obj(body)?.source),
      resultCount: num(obj(data)?.total),
    }),
  },

  // ─── Templates & scoping (EAINT-753 §11) ──────────────────────────
  // Registered now, dormant in v1 — backend/MCP-only, no PWA call sites
  // for these routes yet. Activates automatically once follow-up UI specs
  // (Save-view, Promote, Share) adopt them.
  {
    method: 'POST',
    match: pathExact('/context-views'),
    event: 'view_saved',
    extract: (body) => ({
      ownerSphereKind: obj(body)?.ownerSphereId ? 'identity' : undefined,
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/templates\/[^/]+\/transfer$/),
    event: 'template_promoted',
    extract: (body) => ({
      templateType: str(obj(body)?.templateType),
      fromKind: str(obj(body)?.fromOwnerSphereType),
      toKind: str(obj(body)?.targetSphereType),
    }),
  },
  {
    method: 'POST',
    match: pathMatches(/^\/templates\/[^/]+\/share$/),
    event: 'template_shared',
    extract: (body) => ({
      templateType: str(obj(body)?.templateType),
      subjectKind: str(obj(body)?.subjectType),
    }),
  },
  // Dormant in v1 — `validateTemplateVersion` (design-system/studioApi) has no PWA call site
  // yet. Activates automatically once a Studio "validate before publish" flow adopts it.
  {
    method: 'POST',
    match: pathMatches(/^\/templates\/[^/]+\/versions\/\d+\/validate$/),
    event: 'template_validated',
    extract: (_body, data) => {
      const findings = obj(data)?.findings as Array<{ severity?: string }> | undefined;
      return {
        errors: num(findings?.filter((f) => f.severity === 'error').length),
        warnings: num(findings?.filter((f) => f.severity === 'warning').length),
      };
    },
  },
];

const REDUX_MAPPINGS: ReduxEventMapping[] = [
  // ─── Contexts ──────────────────────────────────────────────────────
  {
    type: 'contexts/createContextThunk/fulfilled',
    event: 'context_created',
    extract: (payload) => ({
      systemName: str(obj(payload)?.systemName),
    }),
    value: (payload) =>
      str(obj(payload)?.id ?? obj(payload)?.systemName),
  },
  {
    type: 'contexts/updateContextThunk/fulfilled',
    event: 'context_updated',
  },
  {
    type: 'contexts/deleteContextThunk/fulfilled',
    event: 'context_deleted',
  },
  {
    type: 'contexts/cloneContextThunk/fulfilled',
    event: 'context_cloned',
  },

  // ─── Notifications ─────────────────────────────────────────────────
  {
    type: 'notifications/markNotificationAsRead/fulfilled',
    event: 'notification_read',
  },

  // ─── Session boot signals ──────────────────────────────────────────
  {
    type: 'userProfile/fetchIdentity/fulfilled',
    event: 'session_identity_loaded',
  },
];

export const apiAnalyticsLogger = createApiResponseLogger(PATTERNS);
export const reduxAnalyticsMiddleware = createAnalyticsMiddleware(REDUX_MAPPINGS);
